import { useState, useRef, useEffect, CSSProperties } from "react"
import { useDraggable, useDndMonitor } from "@dnd-kit/core"
import { Popover } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { SpanningTaskSegment, WEEK_VIEW_SPANNING_ROW_HEIGHT, SPANNING_EVENT_GAP } from "../MonthView/spanningEventHelpers"
import {
  getSpanningTaskOuterStyles,
  getSpanningTaskInnerStyle,
  SpanningTaskContent,
  SpanningTaskResizeHandle,
} from "../MonthView/spanningTaskCardParts"
import { TaskCalendarEvent } from "../scheduleApi"
import TaskPopoverContent from "../TaskPopoverContent"
import { HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"

export type Props = {
  segment: SpanningTaskSegment
  dayColumnWidths: number[]
  onTaskClick: (event: TaskCalendarEvent) => void
  onResize?: (task: TaskCalendarEvent, newEndDate: Dayjs) => void
  onResizingChange?: (isResizing: boolean) => void
  weekStart: Dayjs
}

const UNASSIGNED_COLOR = '#e0e0e0'

const WeekViewSpanningTask = ({ segment, dayColumnWidths, onTaskClick, onResize, onResizingChange, weekStart }: Props) => {
  const { task, startDayIndex, spanWidth, isStart, isEnd, rowIndex, weekIndex } = segment
  const color = task.crewForemanColor || UNASSIGNED_COLOR
  const isCompleted = !!task.completedAt
  const [isHovering, setIsHovering] = useState(false)
  const justFinishedResizing = useRef(false)
  const lastResizeTransformX = useRef<number>(0)

  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `spanning-task-${task.id}-${weekIndex}`,
    data: { event: task, segment, isTask: true },
    disabled: task.frozen,
  })

  const resizeId = `spanning-task-${task.id}-${weekIndex}-resize`
  const {
    attributes: resizeAttributes,
    listeners: resizeListeners,
    setNodeRef: setResizeNodeRef,
    isDragging: isResizing,
    transform: resizeTransform,
  } = useDraggable({
    id: resizeId,
    data: { event: task, segment, isResize: true, isTask: true },
    disabled: task.frozen,
  })

  const taskEnd = dayjs(task.end)
  const resizeDelta = resizeTransform?.x ?? 0

  useEffect(() => {
    if (resizeTransform?.x !== undefined) {
      lastResizeTransformX.current = resizeTransform.x
    }
  }, [resizeTransform?.x])

  useEffect(() => {
    onResizingChange?.(isResizing)
  }, [isResizing, onResizingChange])

  useDndMonitor({
    onDragEnd: (dragEvent) => {
      if (dragEvent.active.id !== resizeId) {
        return
      }
      justFinishedResizing.current = true
      if (onResize && dayColumnWidths.length > 0) {
        const totalDelta = lastResizeTransformX.current
        const absDelta = Math.abs(totalDelta)
        const endDayIdx = segment.endDayIndex

        const columns = totalDelta >= 0
          ? dayColumnWidths.slice(endDayIdx + 1, 7)
          : dayColumnWidths.slice(startDayIndex + 1, endDayIdx + 1).reverse()

        let cumulativeWidth = 0
        const dayCount = columns.reduce((days, width) => {
          if (cumulativeWidth + width / 2 <= absDelta) {
            cumulativeWidth += width
            return days + 1
          }
          return days
        }, 0)

        const deltaDays = totalDelta >= 0 ? dayCount : -dayCount
        const newEndDate = taskEnd.add(deltaDays, 'day')
        const taskStart = dayjs(task.start)
        if (!newEndDate.isBefore(taskStart, 'day') && deltaDays !== 0) {
          onResize(task, newEndDate)
        }
      }
      lastResizeTransformX.current = 0
    },
  })

  const leftPx = dayColumnWidths.slice(0, startDayIndex).reduce((s, w) => s + w, 0)
  let widthPx = dayColumnWidths.slice(startDayIndex, startDayIndex + spanWidth).reduce((s, w) => s + w, 0)

  if (isResizing && dayColumnWidths.length > 0) {
    const absResizeDelta = Math.abs(resizeDelta)
    const originalEndDayIndex = segment.endDayIndex

    const columns = resizeDelta >= 0
      ? dayColumnWidths.slice(originalEndDayIndex + 1, 7)
      : dayColumnWidths.slice(startDayIndex + 1, originalEndDayIndex + 1).reverse()

    let cumulativeWidth = 0
    const dayCount = columns.reduce((days, width) => {
      if (cumulativeWidth + width / 2 <= absResizeDelta) {
        cumulativeWidth += width
        return days + 1
      }
      return days
    }, 0)

    const deltaDays = resizeDelta >= 0 ? dayCount : -dayCount
    const taskStart = dayjs(task.start)
    let newEndDayIndex = originalEndDayIndex + deltaDays

    const minEndDayIndex = Math.max(startDayIndex, taskStart.diff(weekStart.startOf('day'), 'day'))
    newEndDayIndex = Math.max(newEndDayIndex, minEndDayIndex)
    newEndDayIndex = Math.min(newEndDayIndex, 6)

    const newSpanWidth = newEndDayIndex - startDayIndex + 1
    widthPx = dayColumnWidths.slice(startDayIndex, startDayIndex + newSpanWidth).reduce((s, w) => s + w, 0)
  }

  const top = rowIndex * (WEEK_VIEW_SPANNING_ROW_HEIGHT + SPANNING_EVENT_GAP)
  const margin = 2

  const outerStyle: CSSProperties = {
    ...getSpanningTaskOuterStyles({ color, isStart, isEnd }),
    position: "absolute",
    top: top + margin,
    left: leftPx + margin,
    width: widthPx - margin * 2,
    height: WEEK_VIEW_SPANNING_ROW_HEIGHT - margin * 2,
    cursor: isResizing ? "ew-resize" : "pointer",
    zIndex: isDragging || isResizing ? 100 : 1,
    opacity: isDragging ? 0.5 : 1,
    transform: !isResizing && transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    pointerEvents: "auto",
  }

  const innerStyle = getSpanningTaskInnerStyle({ isEnd })

  const dragProps = isResizing ? {} : { ...listeners, ...attributes }

  const contentElements = (
    <SpanningTaskContent
      task={task}
      isStart={isStart}
      isEnd={isEnd}
      isResizing={isResizing}
      isCompleted={isCompleted}
      titleFontSize={12}
    />
  )

  return (
    <Popover
      content={<TaskPopoverContent event={task} />}
      open={isHovering && !isDragging && !isResizing}
      placement="bottom"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setNodeRef}
        {...dragProps}
        onClick={(e) => {
          e.stopPropagation()
          if (justFinishedResizing.current) {
            justFinishedResizing.current = false
            return
          }
          if (!isResizing) {
            onTaskClick(task)
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          if (!isResizing) {
            setIsHovering(false)
          }
        }}
        style={outerStyle}
      >
        {isStart ? <div style={innerStyle}>{contentElements}</div> : contentElements}

        {isEnd && (isHovering || isResizing) && onResize && (
          <SpanningTaskResizeHandle
            setNodeRef={setResizeNodeRef}
            listeners={resizeListeners}
            attributes={resizeAttributes}
            color={color}
          />
        )}
      </div>
    </Popover>
  )
}

export default WeekViewSpanningTask
